import { Component, OnDestroy, OnInit, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { MenuItem } from 'primeng/api';
import { ArtistPortfolioProductsService, ShareableProductLink } from 'src/app/Services/artist-portfolio-products/artist-portfolio-products.service';
import { ArtistProductsService, ArtistProductSummary } from 'src/app/Services/artistproducts.service';
import { Artist, ArtistsService } from 'src/app/Services/artists.service';
import { ApplicationUser, AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { GalleryStaffService } from 'src/app/Services/gallery-staff.service';
import { ShareService } from 'src/app/Services/shared/share.service';
import { SaveGalleryStaffRequest } from 'src/app/models/staff.model';
import { ImageUtils } from 'src/app/shared/utils/image-utils';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'view-artist-profile',
    templateUrl: './view-artist-profile.component.html',
    styleUrls: ['./view-artist-profile.component.css'],
    standalone: false
})
export class ArtistViewProfileComponent implements OnInit, OnDestroy, AfterViewInit {
  public artistId!: string;
  public isAuthenticated: boolean = false;
  public galleryId!: string;
  public artist: Artist = {} as Artist;
  public gallery: ApplicationUser = {} as ApplicationUser;
  public authConfig: AuthConfig = {};
  public artistAddress: string = '';
  public galleryAddress: string = '';
  public items: MenuItem[] | undefined;
  public home: MenuItem | undefined;
  public Staff: SaveGalleryStaffRequest[] = [];
  public isStaffAvailable: boolean = false;
  public userId!: string;
  public favoritePortfolioProducts: ShareableProductLink[] = [];
  public selectedProduct!: ArtistProductSummary;
  public showProductDialog = false;
  public selectedProductId: any;
  public menuOpen = false;
  public showTermsDialog: boolean = false;
  public showOriginalTermsDialog: boolean = false;

  constructor(
    private authService: AuthService,
    private ngxSpinnerService: NgxSpinnerService,
    private artistsService: ArtistsService,
    private galleryStaffService: GalleryStaffService,
    private route: ActivatedRoute,
    private shareService: ShareService,
    private router: Router,
    private artistPortfolioProductsService: ArtistPortfolioProductsService,
    private ArtistProductsService: ArtistProductsService,
    private cdr: ChangeDetectorRef,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.initializeAuthData();
    this.setupRouteSubscription();
    this.loadFavoritesPortfolioProducts();
  }

  ngAfterViewInit(): void {
    this.setupMobileMenu();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateSidebarVisibility(); // Always re-check on resize
  }

  ngOnDestroy(): void {
    this.shareService.updateVisibility(true); // Show sidebar when leaving
  }

  private initializeAuthData(): void {
    this.authConfig = this.authService.getAuthConfig();
    this.isAuthenticated = this.authService.isAuthenticated();
  }

  private setupRouteSubscription(): void {
    this.route.params.subscribe(params => {
      const identifier = params['identifier'];
      if (!identifier) return;

      this.ngxSpinnerService.show();

      // Reset
      this.artist = {} as Artist;
      this.gallery = {} as ApplicationUser;
      this.artistId = '';
      this.galleryId = '';

      // Try Artist first
      this.artistsService.getArtistByUserName(identifier).subscribe({
        next: (artist: Artist) => {
          if (artist && artist.Id) {
            this.handleArtistSuccess(artist, identifier);
          } else {
            this.tryLoadGallery(identifier);
          }
          this.ngxSpinnerService.hide();
        },
        error: () => {
          this.tryLoadGallery(identifier);
          this.ngxSpinnerService.hide();
        }
      });
    });
  }

  private tryLoadGallery(identifier: string): void {
    this.artistsService.getGalleryByuserName(identifier).subscribe({
      next: (gallery: ApplicationUser) => {
        if (gallery && gallery.Id) {
          this.handleGallerySuccess(gallery, identifier);
        } else {
          console.error('Gallery not found');
        }
        this.ngxSpinnerService.hide();
      },
      error: (err) => {
        console.error('Profile not found:', err);
        this.ngxSpinnerService.hide();
      }
    });
  }

  private handleArtistSuccess(artist: Artist, originalIdentifier: string): void {
    if (!artist) return;

    this.artist = artist;
    this.artistId = artist.Id || '';

    this.setupArtistBreadcrumb();
    this.constructArtistAddress();
    this.handleFragmentNavigation();
    this.loadFavoritesPortfolioProducts();

    if (artist.UserName && originalIdentifier !== artist.UserName) {
      this.updateUrlWithUsername(artist.UserName);
    }

    // ARTIST PROFILE → ALWAYS HIDE SIDEBAR
    this.shareService.updateVisibility(false);
  }

  private handleGallerySuccess(gallery: ApplicationUser, originalIdentifier: string): void {
    if (!gallery) return;

    this.gallery = gallery;
    this.galleryId = gallery.Id || '';

    this.setupGalleryBreadcrumb();
    this.constructGalleryAddress();
    this.handleFragmentNavigation();
    this.loadFavoritesPortfolioProducts();

    if (gallery.UserName && originalIdentifier !== gallery.UserName) {
      this.updateUrlWithUsername(gallery.UserName);
    }

    // GALLERY PROFILE → SHOW SIDEBAR (only if authenticated)
    this.updateSidebarVisibility();
  }

  private updateUrlWithUsername(username: string): void {
    this.router.navigate(['profile', username], {
      queryParamsHandling: 'preserve',
      fragment: this.route.snapshot.fragment || undefined,
      replaceUrl: true
    });
  }

  private setupArtistBreadcrumb(): void {
    this.items = [
      { label: this.transloco.translate('viewArtistProfile.viewArtistProfile') },
      { label: this.artist?.Name || this.transloco.translate('viewArtistProfile.artist') }
    ];
    this.home = { icon: 'pi pi-home', routerLink: '/' };
  }

  private constructArtistAddress(): void {
    if (!this.artist) return;

    const addressParts = [
      this.artist.AddressLine1,
      this.artist.AddressLine2,
      this.artist.StreetAddress,
      this.artist.Suburb,
      this.artist.State,
      this.artist.PostCode
    ].filter(part => part && part.trim() !== '');

    this.artistAddress = addressParts.join(', ');
  }

  private setupGalleryBreadcrumb(): void {
    this.home = { icon: 'pi pi-home', routerLink: '/' };
    const name = this.gallery?.BusinessName || this.gallery?.Name || this.transloco.translate('viewArtistProfile.gallery');

    this.items = [
      { label: this.authConfig.IsArtist ? this.transloco.translate('viewArtistProfile.viewGalleryProfile') : this.transloco.translate('viewArtistProfile.viewArtistProfile') },
      { label: name }
    ];
  }

  private constructGalleryAddress(): void {
    if (!this.gallery || this.gallery.GalleryAddresses.length === 0) return;
    this.galleryAddress = this.gallery.GalleryAddresses
      .map(address => {
        const addressParts = [
          address.Address1,
          address.Address2,
          address.AddressType,
          address.SuburbTown,
          address.State,
          address.PostalCode
        ].filter(part => part && part.trim() !== '');

        return addressParts.join(', ');
      })
      .join(', ');
  }

  private loadFavoritesPortfolioProducts(): void {
    const id = this.artistId || this.galleryId;
    if (!id) return;

    this.artistPortfolioProductsService.fetchFavoritePortfoliosProducts(id).subscribe(
      (data: ShareableProductLink[]) => {
        this.favoritePortfolioProducts = data
          .filter(product => product.FavouritePortfolioLinkViewModel?.IsShow !== false)
          .map(product => {
            product.PortfolioProductsViewModel.forEach(item => {
              item.ArtistProduct.ResizedImageUrl = ImageUtils.getResizedCloudinaryUrl(
                item.ArtistProduct.ProductImgUrl || 'assets/images/No-Image.png'
              );
            });
            return product;
          });
      },
      (err) => {
        console.error('Error fetching favorites:', err);
      }
    );
  }

  private handleFragmentNavigation(): void {
    this.route.fragment.subscribe(fragment => {
      if (fragment) {
        setTimeout(() => {
          const element = document.getElementById(fragment);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    });
  }

  private setupMobileMenu(): void {
    const menu = document.getElementById('mobileMenu');
    const overlay = document.getElementById('mobileMenuOverlay');

    if (!menu || !overlay) return;

    const getMenuWidth = () => this.isMobileScreen() ? '100%' : '280px';
    const getMenuRightPosition = (isOpen: boolean) => {
      return isOpen ? '0' : (this.isMobileScreen() ? '-100%' : '-280px');
    };

    const openMenu = () => {
      menu.style.width = getMenuWidth();
      menu.style.right = getMenuRightPosition(true);
      overlay.style.opacity = '1';
      overlay.style.display = 'block';
      this.menuOpen = true;
    };

    const closeMenu = () => {
      menu.style.right = getMenuRightPosition(false);
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 300);
      this.menuOpen = false;
    };

    menu.style.width = getMenuWidth();
    menu.style.right = getMenuRightPosition(false);

    window.addEventListener('resize', () => {
      menu.style.width = getMenuWidth();
      if (!this.menuOpen) {
        menu.style.right = getMenuRightPosition(false);
      }
    });

    document.getElementById('mobileMenuToggle')?.addEventListener('click', (e) => {
      e.preventDefault();
      openMenu();
    });

    document.getElementById('mobileMenuClose')?.addEventListener('click', (e) => {
      e.preventDefault();
      closeMenu();
    });

    document.getElementById('mobileMenuOverlay')?.addEventListener('click', () => {
      closeMenu();
    });

    const menuLinks = document.querySelectorAll('.mobile-menu-link');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => {
        closeMenu();
      });
    });
  }

  public isMobileScreen(): boolean {
    return window.innerWidth <= 768;
  }

  public updateButtonVisibility(): void {
    this.isStaffAvailable = this.Staff && this.Staff.length > 0;
  }

  public closeProductDialog(): void {
    this.showProductDialog = false;
  }

  public showProductDeatils(selectedProductId: string): void {
    this.selectedProductId = selectedProductId;
    this.showProductDialog = true;
  }

  public toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    const menu = document.getElementById('mobileMenu');
    const overlay = document.getElementById('mobileMenuOverlay');

    if (menu && overlay) {
      if (this.menuOpen) {
        menu.style.width = this.isMobileScreen() ? '100%' : '280px';
        menu.style.right = '0';
        overlay.style.opacity = '1';
        overlay.style.display = 'block';
      } else {
        menu.style.right = this.isMobileScreen() ? '-100%' : '-280px';
        overlay.style.opacity = '0';
        setTimeout(() => {
          overlay.style.display = 'none';
        }, 300);
      }
    }
  }

  public navigateToSection(sectionId: string): void {
    this.menuOpen = false;
    this.router.navigate([], {
      relativeTo: this.route,
      fragment: sectionId,
      replaceUrl: true
    });

    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  public showShareDialog: boolean = false;
  public openShareDialog(): void {
    this.showShareDialog = true;
  }

  public onShareDialogClose($event: boolean) {
    this.showShareDialog = $event;
  }

  public openTermsDialog(): void {
    if (this.galleryId && this.gallery) {
      if (!this.gallery.Terms || this.gallery.Terms.trim() === '') {
        this.showTermsDialog = true;
        this.showOriginalTermsDialog = false;
      } else {
        this.showTermsDialog = false;
        this.showOriginalTermsDialog = true;
      }
      this.cdr.detectChanges();
    } else {
      console.error('Gallery ID or gallery data is undefined');
    }
  }

  public onTermsDialogClose(): void {
    this.showTermsDialog = false;
    this.cdr.detectChanges();
  }

  public onOriginalTermsDialogClose(): void {
    this.showOriginalTermsDialog = false;
    this.cdr.detectChanges();
  }

  public onTermsDialogAccepted(): void {
    this.showOriginalTermsDialog = false;
    console.log('Terms accepted for gallery:', this.galleryId);
    this.cdr.detectChanges();
  }

  public onTermsDialogCancelled(): void {
    this.showOriginalTermsDialog = false;
    this.cdr.detectChanges();
  }

  public sendWhatsAppMessage(): void {
    this.router.navigate(['/inbox', this.galleryId]);
  }

  // ========================================
  // === SIDEBAR VISIBILITY LOGIC (FINAL) ===
  // ========================================
  private updateSidebarVisibility(): void {
    // If viewing Gallery profile → Show sidebar (if authenticated)
    if (this.galleryId) {
      this.shareService.updateVisibility(this.isAuthenticated);
    }
    // Artist profile → Always hide
    else if (this.artistId) {
      this.shareService.updateVisibility(false);
    }
    // Default
    else {
      this.shareService.updateVisibility(this.isAuthenticated);
    }
  }
}